import {
  GIFT_FULFILMENT_LABELS,
  MAX_GIFTS_PER_APPLICATION,
  GIFT_DETAILS_MAX_RETENTION_DAYS,
  GIFT_DETAILS_AFTER_SENT_DAYS,
} from '@affily/shared/constants';
import { formatCents } from '@affily/shared/money';
import { COUNTRIES } from '@affily/shared/countries';
import { rule } from '../lib/fieldRules.js';
import { Field, Input, Select, Textarea } from './ui.jsx';

/**
 * Choosing gifts and saying where they should go.
 *
 * Used when applying, and again on a partnership when asking for a gift later,
 * so the delivery form and its wording live here once. What is collected depends
 * on how the gift reaches the creator: an address to ship to, a note for a
 * booking (dates, names), or nothing at all.
 */

export const emptyDetails = () => ({
  name: '',
  line1: '',
  line2: '',
  city: '',
  region: '',
  postalCode: '',
  country: '',
  phone: '',
  bookingNote: '',
});

/** The body fields the API wants for one gift, shaped by how it is fulfilled. */
export function detailsPayload(fulfilment, d) {
  if (fulfilment === 'ship') {
    return {
      shippingAddress: {
        name: d.name,
        line1: d.line1,
        line2: d.line2 || undefined,
        city: d.city,
        region: d.region || undefined,
        postalCode: d.postalCode,
        country: d.country,
        phone: d.phone || undefined,
      },
    };
  }
  if (fulfilment === 'booking') return { bookingNote: d.bookingNote };
  return {};
}

/** Selected rows -> the `gifts` array of POST /applications. */
export const giftsPayload = (selected, gifts) =>
  selected.map((s) => {
    const gift = gifts.find((g) => g.key === s.giftKey);
    return { giftKey: s.giftKey, ...detailsPayload(gift?.fulfilment, s.details) };
  });

export const isOutOfStock = (g) => g.limited && (g.remaining ?? 0) < 1;

export function PrivacyNote({ fulfilment }) {
  if (fulfilment === 'none') return null;
  const ship = fulfilment === 'ship';
  return (
    <p className="text-xs text-ink-muted">
      {ship ? 'Your address stays' : 'These details stay'} on Affily. Only this program’s brand can
      see {ship ? 'it' : 'them'}, and only after they approve the request.{' '}
      {ship ? 'It is' : 'They are'} deleted when you mark the gift received, or if the request is
      declined or cancelled; otherwise {GIFT_DETAILS_AFTER_SENT_DAYS} days after it ships, and at
      most {GIFT_DETAILS_MAX_RETENTION_DAYS} days after you ask.
    </p>
  );
}

/** The delivery fields for ONE gift. `errors` is the server's flat, dotted map. */
export function GiftDetailsForm({ fulfilment, value, onChange, errors = {}, prefix = '' }) {
  const set = (patch) => onChange({ ...value, ...patch });
  const err = (path) => errors[`${prefix}${path}`];

  if (fulfilment === 'none') {
    return <p className="text-xs text-ink-muted">Nothing to collect — the brand will be in touch through the program.</p>;
  }

  if (fulfilment === 'booking') {
    return (
      <div className="space-y-2">
        <Field
          label="Booking details"
          required
          error={err('bookingNote')}
          constraint={rule.max(1000)}
          hint="Dates, how many people, names, anything the brand needs to book you in."
        >
          <Textarea
            rows={3}
            value={value.bookingNote}
            onChange={(e) => set({ bookingNote: e.target.value })}
            maxLength={1000}
          />
        </Field>
        <PrivacyNote fulfilment="booking" />
      </div>
    );
  }

  const f = (path) => `shippingAddress.${path}`;
  return (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Recipient name" required error={err(f('name'))} constraint={rule.max(100)} className="sm:col-span-2">
          <Input value={value.name} onChange={(e) => set({ name: e.target.value })} maxLength={100} autoComplete="name" />
        </Field>
        <Field label="Address line 1" required error={err(f('line1'))} constraint={rule.max(150)}>
          <Input value={value.line1} onChange={(e) => set({ line1: e.target.value })} maxLength={150} autoComplete="address-line1" />
        </Field>
        <Field label="Address line 2" error={err(f('line2'))} constraint={`${rule.optional()} · ${rule.max(150)}`}>
          <Input value={value.line2} onChange={(e) => set({ line2: e.target.value })} maxLength={150} autoComplete="address-line2" />
        </Field>
        <Field label="City" required error={err(f('city'))} constraint={rule.max(100)}>
          <Input value={value.city} onChange={(e) => set({ city: e.target.value })} maxLength={100} autoComplete="address-level2" />
        </Field>
        <Field label="State / region" error={err(f('region'))} constraint={`${rule.optional()} · ${rule.max(100)}`}>
          <Input value={value.region} onChange={(e) => set({ region: e.target.value })} maxLength={100} autoComplete="address-level1" />
        </Field>
        <Field label="Postal code" required error={err(f('postalCode'))} constraint={rule.max(20)}>
          <Input value={value.postalCode} onChange={(e) => set({ postalCode: e.target.value })} maxLength={20} autoComplete="postal-code" />
        </Field>
        <Field label="Country" required error={err(f('country'))} constraint="Choose from the list">
          {/* A list, not a box: a two-letter field turns "United Kingdom" into "UN". */}
          <Select value={value.country} onChange={(e) => set({ country: e.target.value })} autoComplete="country">
            <option value="">Select a country</option>
            {COUNTRIES.map((c) => (
              <option key={c.code} value={c.code}>
                {c.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label="Phone"
          error={err(f('phone'))}
          constraint={`${rule.optional()} · ${rule.max(30)}`}
          hint="Some couriers need one."
          className="sm:col-span-2"
        >
          <Input value={value.phone} onChange={(e) => set({ phone: e.target.value })} maxLength={30} autoComplete="tel" />
        </Field>
      </div>
      <PrivacyNote fulfilment="ship" />
    </div>
  );
}

/**
 * The picker used on the application form.
 *
 * `value` is an ordered array of { giftKey, details }; its order is the order of
 * the `gifts` array sent to the server, which is what makes the server's
 * `gifts.<n>.…` field errors land on the right row.
 */
export function GiftPicker({ gifts, value, onChange, errors = {}, autoApprove = false, currency }) {
  if (!gifts?.length) return null;
  const atMax = value.length >= MAX_GIFTS_PER_APPLICATION;

  const toggle = (g) => {
    const index = value.findIndex((s) => s.giftKey === g.key);
    if (index >= 0) onChange(value.filter((_, i) => i !== index));
    else onChange([...value, { giftKey: g.key, details: emptyDetails() }]);
  };

  return (
    <fieldset className="rounded-lg border border-rule bg-paper-raised p-4">
      <legend className="px-1 text-sm font-semibold">Ask for a gift</legend>
      <p className="mt-1 text-xs text-ink-muted">
        Optional. Pick up to {MAX_GIFTS_PER_APPLICATION}.{' '}
        {autoApprove
          ? 'This program approves creators automatically, so gifts in stock are approved with you.'
          : 'The brand approves each one separately.'}{' '}
        A gift never changes what a conversion pays.
      </p>
      {errors.gifts && <p className="mt-2 text-xs font-medium text-negative">{errors.gifts}</p>}

      <ul className="mt-3 space-y-3">
        {gifts.map((g) => {
          const index = value.findIndex((s) => s.giftKey === g.key);
          const checked = index >= 0;
          const soldOut = isOutOfStock(g);
          const disabled = soldOut || (!checked && atMax);
          return (
            <li key={g.key} className="rounded-md border border-rule bg-paper-sunken p-3">
              <label className={`flex items-start gap-3 ${disabled ? 'opacity-60' : 'cursor-pointer'}`}>
                <input
                  type="checkbox"
                  className="mt-1 size-4 accent-accent"
                  checked={checked}
                  disabled={disabled}
                  onChange={() => toggle(g)}
                />
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-ink">{g.name}</span>
                  <span className="block text-xs text-ink-muted">
                    {GIFT_FULFILMENT_LABELS[g.fulfilment]}
                    {g.retailValueCents != null && ` · ${formatCents(g.retailValueCents, { dropZeroCents: true, currency })} brand-stated value`}
                    {g.limited && !soldOut && ` · ${g.remaining} of ${g.quantityTotal} left`}
                    {soldOut && ' · out of stock'}
                  </span>
                  {g.description && <span className="mt-1 block text-xs text-ink-muted">{g.description}</span>}
                </span>
              </label>

              {checked && (
                <div className="mt-3 border-t border-rule pt-3">
                  <GiftDetailsForm
                    fulfilment={g.fulfilment}
                    value={value[index].details}
                    onChange={(details) =>
                      onChange(value.map((s, i) => (i === index ? { ...s, details } : s)))
                    }
                    errors={errors}
                    prefix={`gifts.${index}.`}
                  />
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </fieldset>
  );
}
